// StatWidget.js
// One aggregation over one column of a `kind: 'data'` entry, rendered as a
// single large number — the spreadsheet SUM of a column and nothing more.
// Anything conditional belongs in a Logic block, where the assistant writes it.

import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { aggregateRows, formatStatValue, isEntryTruncated } from '../../../utils/widgetModel';

function StatWidget({ entry, config = {}, loading = false }) {
  const rows = useMemo(() => (Array.isArray(entry?.rows) ? entry.rows : []), [entry]);

  // A run's preview caps rows at 50. Summing a capped preview would print a
  // confidently WRONG total, so the tile refuses to compute until the caller has
  // supplied the complete stored result.
  const truncated = isEntryTruncated(entry);

  const value = useMemo(
    () => (truncated ? null : aggregateRows(rows, config.column, config.agg)),
    [truncated, rows, config.column, config.agg]
  );

  if (truncated || (loading && value === null)) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  // A result with no rows has nothing to aggregate: the honest readout is "No
  // data", not a dash that reads as a broken tile. Count rows is simply 0.
  if (rows.length === 0 && config.agg !== 'count') {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body1" color="text.secondary">No data</Typography>
      </Box>
    );
  }

  // The label is surfaced as the card title by WidgetCard, so nothing is printed
  // under the number.
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        // A metric card only reads as a tile when the number sits in the middle
        // of it, so the value is centred on both axes rather than just
        // vertically.
        alignItems: 'center',
        minWidth: 0,
      }}
    >
      {/* The exact value, pence and all, at a size a narrow tile can hold;
          wrapping is the last resort for a number wider than the card. */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
          textAlign: 'center',
          overflowWrap: 'anywhere',
          px: 1,
        }}
      >
        {formatStatValue(value, config.format)}
      </Typography>
    </Box>
  );
}

export default StatWidget;
